import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import { AuthTypeValue } from './getAuthenticationTypes';

type OperationItem = {
  operation: OpenAPI.OperationObject;
  method: OpenAPI.HttpMethod;
  path: string;
};

export const getMethodOrder = (method: OpenAPI.HttpMethod): number => {
  switch (method) {
    case OpenAPI.HttpMethod.GET:
      return 0;
    case OpenAPI.HttpMethod.POST:
      return 1;
    case OpenAPI.HttpMethod.PATCH:
      return 2;
    case OpenAPI.HttpMethod.PUT:
      return 3;
    case OpenAPI.HttpMethod.DELETE:
      return 4;
    default:
      return 999;
  }
};

/**
 * Sorts domain strings with apis.roblox.com first, then alphabetically
 */
export const sortDomains = (domains: string[]): string[] => {
  return [...domains].sort((a, b) => {
    if (a === 'apis.roblox.com') return -1;
    if (b === 'apis.roblox.com') return 1;
    return a.localeCompare(b);
  });
};

/**
 * Sorts auth types in the order: roblox-api-key, roblox-oauth2, roblox-legacy-cookie, none
 */
export const sortAuthTypes = (authTypes: AuthTypeValue[]): AuthTypeValue[] => {
  const order: AuthTypeValue[] = [
    'roblox-api-key',
    'roblox-oauth2',
    'roblox-legacy-cookie',
    'none',
  ];
  const orderMap = new Map(order.map((type, index) => [type, index]));
  return [...authTypes].sort((a, b) => {
    const orderA = orderMap.get(a) ?? 999;
    const orderB = orderMap.get(b) ?? 999;
    return orderA - orderB;
  });
};

/**
 * Sorts stability levels in the order: STABLE, BETA, EXPERIMENTAL
 */
export const sortStabilities = (
  stabilities: NonNullable<OpenAPI.OperationObject['x-roblox-stability']>[],
): NonNullable<OpenAPI.OperationObject['x-roblox-stability']>[] => {
  const order: NonNullable<OpenAPI.OperationObject['x-roblox-stability']>[] = [
    'STABLE',
    'BETA',
    'EXPERIMENTAL',
  ];
  const orderMap = new Map(order.map((stability, index) => [stability, index]));
  return [...stabilities].sort((a, b) => {
    const orderA = orderMap.get(a) ?? 999;
    const orderB = orderMap.get(b) ?? 999;
    return orderA - orderB;
  });
};

/**
 * Sorts operations by domain: path alphabetically, then by method order
 */
export const sortOperationsByDomain = (operations: OperationItem[]): OperationItem[] => {
  return [...operations].sort((a, b) => {
    const pathCompare = a.path.localeCompare(b.path);
    if (pathCompare !== 0) {
      return pathCompare;
    }
    return getMethodOrder(a.method) - getMethodOrder(b.method);
  });
};

/**
 * Sorts operations by feature: ordered strictly by the tag order
 */
export const sortOperationsByFeature = (
  operations: OperationItem[],
  document: OpenAPI.Document,
  configKey?: string,
): OperationItem[] => {
  const deepLinkFragmentToOperationMap = new Map<string, OperationItem>();
  operations.forEach((op) => {
    deepLinkFragmentToOperationMap.set(
      OpenAPI.getOperationDeepLinkFragment(document, op.operation, op.method, op.path),
      op,
    );
  });

  const ordering = document.tags?.find((t) => t.name === configKey)?.['x-roblox-tag-info']
    ?.ordering;
  if (!ordering || ordering.length === 0) {
    return operations;
  }

  return ordering
    .map((fragment) => deepLinkFragmentToOperationMap.get(fragment))
    .filter((op) => op !== undefined) as OperationItem[];
};

/**
 * Sorts operations based on page type:
 * - Feature-based: ordered strictly by the tag order
 * - Domain-based: ordered by path alphabetically, then by method
 */
export const sortOperations = (
  operations: OperationItem[],
  document: OpenAPI.Document,
  configType: 'feature' | 'domain',
  configKey?: string,
): OperationItem[] => {
  switch (configType) {
    case 'feature':
      return sortOperationsByFeature(operations, document, configKey);
    case 'domain':
      return sortOperationsByDomain(operations);
    default:
      return operations;
  }
};
